/**
 * Lays the page out in the preview as it is laid out on a real screen. The preview frame is as tall as the whole page, so
 * a section sized by the screen's height (`92vh`) would stretch to the page's height. Here every viewport-height unit
 * becomes a share of a fixed first screen, `--wk-fold`.
 */

/** The height of a guest's first screen: a laptop's, and a phone's. */
export const FOLD = { desktop: 900, phone: 844 } as const;

// Why the skipped kinds come first: a comment, a quoted string, a url() and a media query's condition must stay as written.
const CSS_PART = /\/\*[\s\S]*?\*\/|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|url\([^)]*\)|@(?:media|supports|container)\b[^{;]*|(?<![\w.#-])(-?)(\d*\.?\d+)(?:vh|svh|dvh|lvh)\b/gi;

/** The same CSS with each viewport-height number turned into a share of `--wk-fold`. */
export function foldCss(css: string): string {
  return css.replace(CSS_PART, (hit, sign: string | undefined, n: string | undefined) => (n === undefined ? hit : `calc(${sign ?? ""}${n} * var(--wk-fold) / 100)`));
}

// Why one pass over the markup: a style block and a tag's style attribute are the only places CSS lives, and a script,
// a comment or the page's words must never be touched.
const MARKUP_PART = /<!--[\s\S]*?-->|<script\b[^>]*>[\s\S]*?<\/script>|(<style\b[^>]*>)([\s\S]*?)(<\/style>)|<[a-zA-Z][^\s/>]*(?:"[^"]*"|'[^']*'|[^'">])*>/gi;
const STYLE_ATTR = /(\sstyle\s*=\s*)(?:"([^"]*)"|'([^']*)')/i;

function foldTag(tag: string): string {
  return tag.replace(STYLE_ATTR, (_, head: string, dq: string | undefined, sq: string | undefined) => (dq !== undefined ? `${head}"${foldCss(dq)}"` : `${head}'${foldCss(sq ?? "")}'`));
}

/** The page's document with viewport-height units folded, and `--wk-fold` set on its root element. */
export function foldDoc(doc: string, fold: number): string {
  const folded = doc.replace(MARKUP_PART, (hit, open: string | undefined, css: string | undefined, close: string | undefined) => {
    if (open !== undefined) return `${open}${foldCss(css ?? "")}${close ?? ""}`;
    return hit.startsWith("<!--") || /^<script\b/i.test(hit) ? hit : foldTag(hit);
  });
  const root = `--wk-fold:${fold}px`;
  const html = /<html\b(?:"[^"]*"|'[^']*'|[^'">])*>/i.exec(folded);
  if (!html) return `<style>:root{${root}}</style>${folded}`;
  const tag = html[0];
  const withVar = STYLE_ATTR.test(tag) ? tag.replace(STYLE_ATTR, (_, head: string, dq: string | undefined, sq: string | undefined) => `${head}"${root};${dq ?? sq ?? ""}"`) : `${tag.slice(0, -1)} style="${root}">`;
  return folded.slice(0, html.index) + withVar + folded.slice(html.index + tag.length);
}
